/**
 * AnnoteIndex tracks relationships between annotes and their connections.
 *
 * When displaying notes that link to annotes, we want to skip over them and
 * add what the annote links to to the surface notes for links and backlinks.
 */
import { storageLogger } from '@annote/core/utils/debug';
import { Note, UUID, isAnnote, AnnoteIndexEntry } from '@annote/types';
import { IDatabase } from '@annote/core/dbs/types';

export interface AnnoteIndex {
  initialize(annoteDB: IDatabase): Promise<void>;

  // Get annote index entry
  get(annoteId: UUID): AnnoteIndexEntry | undefined;

  // Update index when notes change
  upsertNote(note: Note): void;
  upsertNotes(notes: Note[]): void;

  // Remove note from index
  removeNote(noteId: UUID): void;

  // Expand skip-over connections in specified direction
  expandSkipOvers(startIds: UUID[], direction: 'links' | 'backlinks'): UUID[];

  // Helper: Get annote IDs from a list of mixed IDs
  getAnnoteIds(ids: UUID[]): UUID[];

  // Clear the index
  clear(): void;

  // Get debug info
  size(): number;
}

export class AnnoteIndexImpl implements AnnoteIndex {
  private index: Map<UUID, AnnoteIndexEntry> = new Map();

  // Initialize the index with data from the database
  async initialize(annoteDB: IDatabase): Promise<void> {
    try {
      const indexData = await annoteDB.getAnnoteIndex();
      storageLogger.debug('AnnoteIndex initialized with', indexData);
      this.index = new Map(Object.entries(indexData));

      storageLogger.debug(`AnnoteIndex initialized with ${this.index.size} annote entries`);
      // The actual implementation would populate a global index instance
      // For now, we log that initialization is ready for when instances are created
    } catch (error) {
      storageLogger.warn('Failed to initialize AnnoteIndex from database, will build incrementally:', error);
    }
  }

  get(annoteId: UUID): AnnoteIndexEntry | undefined {
    return this.index.get(annoteId);
  }

  upsertNote(note: Note): void {
    // NOTE: This keeps the index up to date but is LOW PRIORITY
    // Annotes don't change frequently and the cost of missing updates is very low
    // (some hidden cards may not show until refresh - acceptable tradeoff)
    // If this causes loops or extra refreshes, consider making annoteIndex more static
    if (!isAnnote(note)) {
      // If it's not an annote but was previously indexed, remove it
      this.removeNote(note.id);
      return;
    }

    const entry: AnnoteIndexEntry = {
      links: [...(note.links || [])],
      backlinks: [...(note.backlinks || [])],
    };

    // Update main index
    this.index.set(note.id, entry);
  }

  upsertNotes(notes: Note[]): void {
    notes.forEach((note) => this.upsertNote(note));
  }

  removeNote(noteId: UUID): void {
    this.index.delete(noteId);
  }

  expandSkipOvers(startIds: UUID[], direction: 'links' | 'backlinks'): UUID[] {
    // For each id in startIds:
    // 1. If it's an annote: exclude it from results, but include sources/takeaways it connects to
    // 2. If it's not an annote: include it in the results
    const result = new Set<UUID>();

    console.log('expanding skip-overs', startIds, direction);
    startIds.forEach((id) => {
      const annote = this.index.get(id);
      if (annote) {
        console.log('expanding annote', id, direction, annote.links, annote.backlinks);
        if (direction === 'links') {
          annote.links.forEach((lid) => {
            console.log('expanding link', lid);
            result.add(lid);
          });
        } else {
          annote.backlinks.forEach((bid) => {
            console.log('expanding backlink', bid);
            result.add(bid);
          });
        }
      } else {
        result.add(id);
      }
    });
    return Array.from(result);
  }

  getAnnoteIds(ids: UUID[]): UUID[] {
    return ids.filter((id) => this.index.has(id));
  }

  clear(): void {
    this.index.clear();
  }

  size(): number {
    return this.index.size;
  }
}

export const annoteIndex = new AnnoteIndexImpl();

// For testing purposes
export const createAnnoteIndex = (): AnnoteIndex => new AnnoteIndexImpl();
